import React, { useCallback, useEffect, useState } from 'react';
import { Box, Separator } from '@chakra-ui/react';
import {
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import { useCoachUserNotes } from '../../api/healthCoach/useCoachUserNotes';
import { Loader } from '../Loader';
import { useParams } from 'react-router-dom';
import { useUserSymptom } from '../../api/healthCoach/useUserSymptom';
import { useSymptomSelect } from '../../api/healthCoach/useSymptomSelect';
import { useUpdateCoachUserNotes } from '../../api/healthCoach/mutations/useUpdateCoachUserNotes';
import { useUpdateUserDiagnosisAndSymptom } from '../../api/healthCoach/mutations/useUpdateUserDiagnosisAndSymptom';
import { Entry, ListWithNotes } from './ListWithNotes';
import { useAffiliate } from '../../api/affiliates/useAffiliate';
import { useDashboardPageState } from '../../hooks/useDashboardPageState';
import { useUserDiagnosis } from '../../api/healthCoach/useUserDiagnosis';
import { EditableDiagnosesList } from './EditableDiagnosesList';

const convertSelectOptionsToDiagnosisAndSymptom = (dxOptions, sxOptions) => {
  const diagnosis = dxOptions
    .filter((d) => !!d.value)
    .map((d) => ({
      id: d.value,
      diagnosis: d.label,
    }));

  const symptom = sxOptions
    .filter((s) => !!s.value)
    .map((s) => ({
      id: s.value,
      symptom: s.label,
    }));

  return { diagnosis, symptom };
};

const convertDiagnosisAndSymptomToSelectOptions = (diagnosis, symptom) => {
  const dxOptions = diagnosis?.map(({ id, diagnosis }) => ({
    label: diagnosis,
    value: id,
  }));
  const sxOptions = symptom?.map(({ id, symptom }) => ({
    label: symptom,
    value: id,
  }));

  return { dxOptions, sxOptions };
};

export const UserInformation = () => {
  const {
    state: { user },
  } = useDashboardPageState();
  const { patient } = useParams();
  const { userNotes, isLoading, isError } = useCoachUserNotes(patient ?? '');
  const { mutation } = useUpdateCoachUserNotes();
  const { loadSymptomOptions } = useSymptomSelect();

  const { data: userDiagnosis, isLoading: isUserDiagnosisLoading } =
    useUserDiagnosis(patient);

  const { data: userSymptom, isLoading: isUserSymptomLoading } =
    useUserSymptom(patient);

  const { affiliate: healthSystem } = useAffiliate({
    affiliateId: user?.health_system,
  });

  const { mutateUserDiagnosisAndSymptom } = useUpdateUserDiagnosisAndSymptom(
    patient ?? '',
    userDiagnosis,
    userSymptom
  );

  const [isEditingSx, setIsEditingSx] = useState(false);

  const [sxValidationMessage, setSxValidationMessage] = useState('');

  const [dxGeneralNotes, setDxGeneralNotes] = useState('');
  const [sxGeneralNotes, setSxGeneralNotes] = useState('');

  const [dxSelectOptions, setDxSelectOptions] = useState<Entry[]>([]);
  const [sxSelectOptions, setSxSelectOptions] = useState<Entry[]>([]);

  const canAddNewDx = dxSelectOptions?.every((opt) => !!opt.value);
  const canAddNewSx = sxSelectOptions?.every((opt) => !!opt.value);

  const updateNotesWithFetchedVals = useCallback(() => {
    if (!userNotes) return;

    const { dx_notes, sx_notes } = userNotes;

    setDxGeneralNotes(dx_notes);
    setSxGeneralNotes(sx_notes);
  }, [userNotes]);

  const updateDiagnosesWithFetchedVals = useCallback(() => {
    if (!userDiagnosis || !userSymptom) return;

    const { dxOptions, sxOptions } = convertDiagnosisAndSymptomToSelectOptions(
      userDiagnosis,
      userSymptom
    );

    setDxSelectOptions(dxOptions);
    setSxSelectOptions(sxOptions);
  }, [userDiagnosis, userSymptom]);

  useEffect(() => {
    if (canAddNewSx) setSxValidationMessage('');
  }, [canAddNewDx, canAddNewSx]);

  useEffect(updateNotesWithFetchedVals, [updateNotesWithFetchedVals]);
  useEffect(updateDiagnosesWithFetchedVals, [updateDiagnosesWithFetchedVals]);

  const saveChanges = async () => {
    const { diagnosis, symptom } = convertSelectOptionsToDiagnosisAndSymptom(
      dxSelectOptions,
      sxSelectOptions
    );

    // first save coach user notes
    await mutation.mutateAsync({
      method: !!userNotes ? 'PATCH' : 'POST',
      userNotes: {
        id: !!userNotes ? userNotes.id : undefined,
        user_id: patient,
        sx_notes: sxGeneralNotes,
        dx_notes: dxGeneralNotes,
      },
    });

    // then save the user associated diagnoses and symptoms
    await mutateUserDiagnosisAndSymptom.mutateAsync({
      diagnosis,
      symptom,
    });

    // clean up in case of save with empty values that did not perform any network request
    const cleanSx = sxSelectOptions.filter((o) => !!o.value);
    const cleanDx = dxSelectOptions.filter((o) => !!o.value);
    setSxSelectOptions(cleanSx);
    setDxSelectOptions(cleanDx);

    setIsEditingSx(false);
  };

  const discardChanges = () => {
    setIsEditingSx(false);
    updateNotesWithFetchedVals();
    updateDiagnosesWithFetchedVals();
  };

  if (!user || isLoading || isUserDiagnosisLoading || isUserSymptomLoading) {
    return (
      <Box p={0}>
        <Loader loading={true} />
      </Box>
    );
  }

  const {
    pregnant_when_onboarded,
    history_of_disordered_eating,
    history_of_gi_surgery,
  } = user;

  const pregnantWhenOnboarded =
    !pregnant_when_onboarded || pregnant_when_onboarded === 'No'
      ? null
      : pregnant_when_onboarded === 'Planning'
      ? 'Planning Pregnancy'
      : pregnant_when_onboarded === 'Nursing'
      ? 'Nursing'
      : 'Pregnant';
  const giSurgeryWhenOnboarded = history_of_gi_surgery ? 'GI Surgery' : null;
  const eatingDisorderWhenOnboarded = history_of_disordered_eating
    ? 'Eating Disorder'
    : null;
  const reportedAtOnboarding = [
    pregnantWhenOnboarded,
    giSurgeryWhenOnboarded,
    eatingDisorderWhenOnboarded,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <VStack mt={5}>
      {(isError || mutation.isError) && (
        <Text mb={6} fontSize={'sm'} color={'error.600'}>
          An error occurred while trying to load or save the coach notes: please
          refresh the page and try again.
        </Text>
      )}

      <VStack>
        <Separator my={3} />

        <EditableDiagnosesList patient={patient} />
      </VStack>

      <Separator my={3} />

      <VStack>
        <ListWithNotes
          entries={sxSelectOptions}
          errorMessage={sxValidationMessage}
          isEditing={isEditingSx}
          isSaving={
            mutation.isLoading || mutateUserDiagnosisAndSymptom.isLoading
          }
          label={'Symptoms'}
          loadOptions={loadSymptomOptions}
          notes={sxGeneralNotes}
          onAddEntry={(newEntry) => {
            if (!canAddNewSx) {
              setSxValidationMessage(
                'Please fill the empty option below before adding a new one.'
              );
              return;
            }

            const newSx = [newEntry, ...sxSelectOptions];
            setSxSelectOptions(newSx);
          }}
          onDiscard={discardChanges}
          onEditList={() => {
            setIsEditingSx((isEditingState) => !isEditingState);
          }}
          onEntriesChange={setSxSelectOptions}
          onNotesChange={setSxGeneralNotes}
          onSave={saveChanges}
          showEditButton
        />
      </VStack>

      <Separator my={3} />

      <VStack>
        <ListWithNotes
          entries={[{ label: 'Coming Soon', value: '' }]}
          label={'Risk Strat'}
        />
      </VStack>

      <Separator my={3} />

      <VStack>
        <ListWithNotes
          entries={[{ label: `${healthSystem?.name ?? '--'}`, value: '' }]}
          label={'Health System'}
        />
      </VStack>

      <Separator my={3} />

      <VStack>
        <ListWithNotes
          entries={[{ label: 'Coming Soon', value: '' }]}
          label={'Referring'}
        />
      </VStack>

      <Separator my={3} />

      <VStack>
        <ListWithNotes
          entries={[{ label: reportedAtOnboarding || 'Nothing', value: '' }]}
          label={'Reported at Onboarding'}
        />
      </VStack>

      <Separator mt={3} mb={8} />
    </VStack>
  );
};
